import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL, expenseAPI, recurringAPI, vendorAPI } from '../api/client';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastProvider';
import { getLogo } from '../utils/logoUpload';
import RecordPreviewModal from '../components/RecordPreviewModal';

// Define expense categories
const EXPENSE_CATEGORIES = [
  'Office Supplies',
  'Travel',
  'Equipment',
  'Utilities',
  'Meals & Entertainment',
  'Marketing',
  'Professional Services',
  'Software & Subscriptions',
  'Insurance',
  'Rent/Lease',
  'Supplies',
  'Vehicle/Transportation',
  'Maintenance & Repairs',
  'Advertising',
  'Consulting',
  'Training',
  'Legal & Accounting',
  'Postage',
  'Internet',
  'VPS Server',
  'Other'
];

// Define payment modes
const PAYMENT_MODES = [
  'Cash',
  'Credit Card',
  'Debit Card',
  'Bank Transfer',
  'Check',
  'Digital Wallet',
  'PayPal',
  'Wire Transfer',
  'Other'
];

function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [formData, setFormData] = useState({ transaction_id: '', category: '', amount: '', description: '', date: '', tax_amount: '', payment_mode: '', receipt_file_id: '', receipt_url: '' });
  const [recurringFormData, setRecurringFormData] = useState({
    vendor_id: '',
    template_name: '',
    category: '',
    amount: '',
    tax_amount: '',
    description: '',
    payment_mode: 'Other',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    next_expense_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileError, setFileError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecurringId, setEditingRecurringId] = useState(null);
  const [isEditingRecurring, setIsEditingRecurring] = useState(false);
  const [previewExpense, setPreviewExpense] = useState(null);
  
  // Pagination & Filter State
  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('DESC');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const { showToast } = useToast();

  const loadExpenses = useCallback(async () => {
    try {
      const params = {
        page,
        limit,
        sortBy,
        order,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(categoryFilter && { category: categoryFilter })
      };
      
      const response = await expenseAPI.getAll(params);
      setExpenses(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setPages(response.data.pagination?.pages || 0);
    } catch (err) {
      console.error('Failed to load expenses', err);
    }
  }, [page, limit, sortBy, order, startDate, endDate, categoryFilter]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    loadRecurringExpenses();
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await vendorAPI.getAll({ limit: 1000 });
      setVendors(response.data.data || response.data || []);
    } catch (err) {
      console.error('Failed to load vendors', err);
    }
  };

  const loadRecurringExpenses = async () => {
    try {
      const response = await recurringAPI.getExpenses();
      setRecurringExpenses(response.data || []);
    } catch (err) {
      console.error('Failed to load recurring expenses', err);
    }
  };

  const resetRecurringForm = () => {
    setRecurringFormData({
      vendor_id: '',
      template_name: '',
      category: '',
      amount: '',
      tax_amount: '',
      description: '',
      payment_mode: 'Other',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      next_expense_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
    setIsEditingRecurring(false);
    setEditingRecurringId(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const fileName = file.name.toLowerCase();
    if (!allowedTypes.includes(file.type) && !fileName.endsWith('.png') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && !fileName.endsWith('.pdf')) {
      setFileError('Only PDF, PNG, and JPG files are allowed');
      e.target.value = '';
      return;
    }

    setFileError('');
    setIsProcessing(true);

    try {
      // Upload receipt to server
      const formDataUpload = new FormData();
      formDataUpload.append('receipt', file);

      const response = await fetch(`${API_BASE_URL}/expenses/upload-receipt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formDataUpload
      }).then(r => r.json());

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          receipt_file_id: response.receipt.filePath,
          receipt_url: response.receipt.downloadUrl,
          description: prev.description || `Receipt: ${response.receipt.fileName}`
        }));
        setFileError('');
      } else {
        setFileError(response.error || 'Failed to upload receipt');
      }
    } catch (err) {
      setFileError('Failed to upload receipt: ' + err.message);
      console.error('Receipt upload error:', err);
    } finally {
      setIsProcessing(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && editingId) {
        // Update existing expense
        await expenseAPI.update(editingId, formData);
      } else {
        // Create new expense
        await expenseAPI.create(formData);
      }
      setFormData({ transaction_id: '', category: '', amount: '', description: '', date: '', tax_amount: '', payment_mode: '', receipt_file_id: '', receipt_url: '' });
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      loadExpenses();
      showToast({
        title: isEditing ? 'Expense updated' : 'Expense created',
        message: 'The expense has been saved successfully.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to save expense', err);
      showToast({
        title: 'Expense not saved',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditingRecurring && editingRecurringId) {
        await recurringAPI.updateExpense(editingRecurringId, {
          ...recurringFormData,
          vendor_id: recurringFormData.vendor_id || null,
          tax_amount: recurringFormData.tax_amount || 0,
          end_date: recurringFormData.end_date || null,
          next_expense_date: recurringFormData.next_expense_date || recurringFormData.start_date,
        });
      } else {
        await recurringAPI.createExpense({
          ...recurringFormData,
          vendor_id: recurringFormData.vendor_id || null,
          tax_amount: recurringFormData.tax_amount || 0,
          end_date: recurringFormData.end_date || null,
        });
      }
      resetRecurringForm();
      setShowRecurringForm(false);
      loadRecurringExpenses();
      showToast({
        title: isEditingRecurring ? 'Recurring expense updated' : 'Recurring expense saved',
        message: 'The recurring expense template has been saved successfully.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to save recurring expense', err);
      showToast({
        title: 'Schedule not saved',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      transaction_id: expense.transaction_id || '',
      category: expense.category,
      amount: expense.amount,
      description: expense.description || '',
      date: expense.date,
      tax_amount: expense.tax_amount || '',
      payment_mode: expense.payment_mode || '',
      receipt_file_id: expense.receipt_file_id || '',
      receipt_url: expense.receipt_url || ''
    });
    setEditingId(expense.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setFormData({ transaction_id: '', category: '', amount: '', description: '', date: '', tax_amount: '', payment_mode: '', receipt_file_id: '', receipt_url: '' });
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await expenseAPI.delete(id);
        loadExpenses();
        showToast({
          title: 'Expense deleted',
          message: 'The expense entry has been removed.',
          tone: 'success',
        });
      } catch (err) {
        console.error('Failed to delete expense', err);
        showToast({
          title: 'Delete failed',
          message: err.response?.data?.error || 'Please try again.',
          tone: 'error',
        });
      }
    }
  };

  const handleDownloadPDF = async (expenseId) => {
    try {
      const logo = getLogo();
      const response = await fetch(
        `${API_BASE_URL}/expenses/${expenseId}/generate-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ logo })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'Expense.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        title: 'PDF downloaded',
        message: 'Expense PDF has been downloaded successfully.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to download expense PDF', err);
      showToast({
        title: 'Download failed',
        message: err.message || 'Failed to generate PDF. Please try again.',
        tone: 'error',
      });
    }
  };

  const handleRecurringStatusUpdate = async (id, recurringExpense, status) => {
    try {
      await recurringAPI.updateExpense(id, {
        ...recurringExpense,
        status,
      });
      loadRecurringExpenses();
      showToast({
        title: 'Recurring expense updated',
        message: `Schedule is now ${status}.`,
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to update recurring expense', err);
      showToast({
        title: 'Update failed',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Ensure date is in YYYY-MM-DD format for HTML input
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const handleEditRecurringExpense = (expense) => {
    setRecurringFormData({
      vendor_id: expense.vendor_id || '',
      template_name: expense.template_name || '',
      category: expense.category,
      amount: expense.amount,
      tax_amount: expense.tax_amount || '',
      description: expense.description || '',
      payment_mode: expense.payment_mode || 'Other',
      frequency: expense.frequency,
      start_date: formatDateForInput(expense.start_date),
      next_expense_date: formatDateForInput(expense.next_expense_date),
      end_date: formatDateForInput(expense.end_date),
    });
    setEditingRecurringId(expense.id);
    setIsEditingRecurring(true);
    setShowRecurringForm(true);
  };

  const handleCancelEditRecurringExpense = () => {
    resetRecurringForm();
    setShowRecurringForm(false);
  };

  const handleDeleteRecurringExpense = async (id) => {
    if (!window.confirm('Delete this recurring expense schedule?')) {
      return;
    }

    try {
      await recurringAPI.deleteExpense(id);
      loadRecurringExpenses();
      showToast({
        title: 'Schedule deleted',
        message: 'The recurring expense template has been removed.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to delete recurring expense', err);
      showToast({
        title: 'Delete failed',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  return (
    <Layout pageTitle="Expenses" pageSubtitle="Manage your business expenses and deductions">
      <div className="flex justify-end mb-8">
        <button 
          onClick={() => { setFormData({ transaction_id: '', category: '', amount: '', description: '', date: '', tax_amount: '', payment_mode: '' }); setIsEditing(false); setEditingId(null); setShowForm(true); }}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20"
        >
          {showForm ? 'Close' : '+ New Expense'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-900">{isEditing ? 'Edit Expense' : 'Create New Expense'}</h3>
            <button 
              onClick={handleCancelEdit}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
            >
              ×
            </button>
          </div>
          
          {fileError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{fileError}</p>
            </div>
          )}

          {/* File Upload Section */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-dashed border-blue-300">
            <label className="block mb-3">
              <span className="block text-sm font-medium text-gray-700 mb-3">📄 Upload Receipt (PDF, PNG, JPG)</span>
              <span className="text-xs text-gray-600 mb-4 block">Upload receipts and invoices for your expense records</span>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
              />
            </label>
            {isProcessing && (
              <div className="mt-3 flex items-center text-blue-600">
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">Uploading receipt...</span>
              </div>
            )}
            {formData.receipt_url && (
              <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                <p className="text-sm text-green-700 mb-2">✓ Receipt attached!</p>
                <div className="flex gap-3">
                  <a href={formData.receipt_url} download target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700 underline">
                    📥 Download Receipt
                  </a>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, receipt_file_id: '', receipt_url: '' }))}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transaction ID / Invoice #</label>
                <input
                  type="text"
                  placeholder="INV-001 or TX-12345"
                  value={formData.transaction_id}
                  onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition bg-white"
                  required
                >
                  <option value="">-- Select Category --</option>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Amount</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.tax_amount}
                  onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount</label>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 flex items-center">
                  <span className="text-lg font-bold text-gray-900">
                    ₹{(parseFloat(formData.amount || 0) + parseFloat(formData.tax_amount || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                <select
                  value={formData.payment_mode}
                  onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition bg-white"
                >
                  <option value="">-- Select Payment Mode --</option>
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
                />
              </div>
              <button type="submit" className="md:col-span-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20">
                Create Expense
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Recurring Expense Templates</h3>
            <p className="text-sm text-gray-500">Schedule repeating costs so the system creates entries automatically.</p>
          </div>
          <div className="flex gap-2">
            {isEditingRecurring && (
              <button
                type="button"
                onClick={handleCancelEditRecurringExpense}
                className="rounded-lg bg-gray-400 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-500"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (showRecurringForm) {
                  resetRecurringForm();
                }
                setShowRecurringForm((current) => !current);
              }}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              {showRecurringForm ? 'Close Schedule Form' : '+ New Recurring Expense'}
            </button>
          </div>
        </div>

        {showRecurringForm && (
          <form onSubmit={handleRecurringSubmit} className="mt-6 border-t border-gray-100 pt-6">
            {isEditingRecurring && (
              <div className="mb-4 rounded-lg bg-blue-50 p-3 border border-blue-200">
                <p className="text-sm font-medium text-blue-900">Editing recurring expense template</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Template Name *</label>
                <input
                  type="text"
                  value={recurringFormData.template_name}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, template_name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  placeholder="Office rent"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Frequency *</label>
                <select
                  value={recurringFormData.frequency}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, frequency: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi-annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Category *</label>
                <select
                  value={recurringFormData.category}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, category: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  required
                >
                  <option value="">-- Select Category --</option>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Vendor</label>
                <select
                  value={recurringFormData.vendor_id}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, vendor_id: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                >
                  <option value="">-- Optional Vendor --</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={recurringFormData.amount}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, amount: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Tax Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={recurringFormData.tax_amount}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, tax_amount: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  value={recurringFormData.start_date}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, start_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              {isEditingRecurring && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Next Run Date</label>
                  <input
                    type="date"
                    value={recurringFormData.next_expense_date}
                    onChange={(e) => setRecurringFormData({ ...recurringFormData, next_expense_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Override the next scheduled date if needed</p>
                </div>
              )}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={recurringFormData.end_date}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, end_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Payment Mode</label>
                <select
                  value={recurringFormData.payment_mode}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, payment_mode: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                >
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  value={recurringFormData.description}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-red-500"
                  placeholder="Monthly software subscription"
                />
              </div>

              <button
                type="submit"
                className="md:col-span-2 rounded-lg bg-emerald-500 px-6 py-2 font-medium text-white transition hover:bg-emerald-600"
              >
                {isEditingRecurring ? 'Update Recurring Expense' : 'Save Recurring Expense'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Template</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Frequency</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Next Run</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recurringExpenses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500">
                    No recurring expense schedules yet.
                  </td>
                </tr>
              ) : recurringExpenses.map((recurringExpense) => (
                <tr key={recurringExpense.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{recurringExpense.template_name || `Schedule #${recurringExpense.id}`}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {recurringExpense.category}
                    {recurringExpense.vendor_name ? ` · ${recurringExpense.vendor_name}` : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{recurringExpense.frequency}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{new Date(recurringExpense.next_expense_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">₹{parseFloat(recurringExpense.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={recurringExpense.status || 'active'}
                      onChange={(e) => handleRecurringStatusUpdate(recurringExpense.id, recurringExpense, e.target.value)}
                      className="rounded-lg border-0 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800 outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditRecurringExpense(recurringExpense)}
                        className="font-medium text-blue-600 transition hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRecurringExpense(recurringExpense.id)}
                        className="font-medium text-rose-600 transition hover:text-rose-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters & Search</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white"
            >
              <option value="">All Categories</option>
              {EXPENSE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white"
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="category">Category</option>
              </select>
              <button
                onClick={() => setOrder(order === 'ASC' ? 'DESC' : 'ASC')}
                className={`px-4 py-2 rounded-lg font-medium ${order === 'DESC' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}
              >
                {order === 'DESC' ? '↓' : '↑'}
              </button>
            </div>
          </div>
        </div>
        {(startDate || endDate || categoryFilter) && (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); setCategoryFilter(''); setPage(1); }}
            className="mt-4 px-4 py-2 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Transaction ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Category</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tax</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Total</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Payment Mode</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Receipt</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Description</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr
                  key={expense.id}
                  className="hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => setPreviewExpense(expense)}
                >
                  <td className="px-6 py-4 text-sm text-gray-900">#{expense.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{expense.transaction_id || '—'}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{expense.category}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{parseFloat(expense.amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">₹{parseFloat(expense.tax_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">₹{(parseFloat(expense.amount || 0) + parseFloat(expense.tax_amount || 0)).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{expense.payment_mode || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{new Date(expense.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm">
                    {expense.receipt_url ? (
                      <a href={expense.receipt_url} download target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">
                        📥 Download
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{expense.description}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPDF(expense.id);
                        }}
                        className="text-green-500 hover:text-green-700 font-medium text-xs transition"
                        title="Download expense PDF with company details"
                      >
                        📄 PDF
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(expense);
                        }} 
                        className="text-blue-500 hover:text-blue-700 font-medium text-xs transition"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(expense.id);
                        }} 
                        className="text-red-500 hover:text-red-700 font-medium text-xs transition"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Info and Controls */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{expenses.length === 0 ? 0 : (page - 1) * limit + 1}</span> to <span className="font-semibold">{Math.min(page * limit, total)}</span> of <span className="font-semibold">{total}</span> expenses
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-400 transition"
            >
              Previous
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-2 rounded-lg transition ${
                    p === page
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPage(Math.min(pages, page + 1))}
              disabled={page === pages}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-400 transition"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {previewExpense && (
        <RecordPreviewModal
          title={`Expense #${previewExpense.id}`}
          subtitle="Expense Record"
          onClose={() => setPreviewExpense(null)}
          fields={[
            { label: 'Transaction ID', value: previewExpense.transaction_id },
            { label: 'Category', value: previewExpense.category },
            { label: 'Amount', value: `₹${parseFloat(previewExpense.amount || 0).toFixed(2)}` },
            { label: 'Tax', value: `₹${parseFloat(previewExpense.tax_amount || 0).toFixed(2)}` },
            { label: 'Total', value: `₹${(parseFloat(previewExpense.amount || 0) + parseFloat(previewExpense.tax_amount || 0)).toFixed(2)}` },
            { label: 'Payment Mode', value: previewExpense.payment_mode },
            { label: 'Date', value: previewExpense.date ? new Date(previewExpense.date).toLocaleDateString() : 'N/A' },
            { label: 'Description', value: previewExpense.description },
            { label: 'Receipt URL', value: previewExpense.receipt_url },
          ]}
        />
      )}
    </Layout>
  );
}

export default Expenses;
